import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { auth } from '@/api/driver'
import type { AuthResult, User } from '@/api/types'
import { session } from './session'

interface AuthState {
  user: User | null
  login: (identifier: string, password: string) => Promise<User>
  /** Store the tokens from /auth/register. */
  signIn: (res: AuthResult) => void
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [user, setUser] = useState<User | null>(() => session.get()?.user ?? null)
  useEffect(() => {
    let owner = session.get()?.user.id
    return session.subscribe((s) => {
      if (owner !== s?.user.id) {
        // Cancel first, then remove data and mutation results before rendering
        // the next identity. Public map/catalog data remains safe to reuse.
        const filters = { predicate: (q: { queryKey: readonly unknown[] }) => !['map', 'catalog', 'reviews'].includes(String(q.queryKey[0])) }
        void qc.cancelQueries(filters)
        qc.removeQueries(filters)
        qc.getMutationCache().clear()
        owner = s?.user.id
      }
      setUser(s?.user ?? null)
    })
  }, [qc])

  const signIn = useCallback((res: AuthResult) => session.set({ accessToken: res.access_token, refreshToken: res.refresh_token, user: res.user }), [])
  const login = useCallback(
    async (identifier: string, password: string) => {
      const res = await auth.login(identifier.trim(), password)
      signIn(res)
      return res.user
    },
    [signIn],
  )
  const logout = useCallback(async () => {
    const pending = auth.logout()
    session.set(null)
    try {
      await pending
    } catch {
      /* the token may already be gone */
    }
  }, [])
  const refreshUser = useCallback(async () => {
    const me = await auth.me()
    const s = session.get()
    if (s) session.set({ ...s, user: me })
  }, [])

  useEffect(() => {
    if (session.get()) refreshUser().catch(() => undefined)
  }, [refreshUser])

  const value = useMemo(() => ({ user, login, signIn, logout, refreshUser }), [user, login, signIn, logout, refreshUser])
  return <Ctx.Provider key={user?.id ?? 'guest'} value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth outside AuthProvider')
  return ctx
}
